import { useEffect, useState } from "react";
import api, { fmtErr } from "../../../api";
import { useSettings } from "./useSettings";
import { useAuth } from "../../../AuthContext";
import { toast } from "sonner";

export default function SettingsCrew() {
  const { s, setS, save } = useSettings();
  const { user } = useAuth();
  const [dispatchers, setDispatchers] = useState([]);
  const [dForm, setDForm] = useState({ name: "", email: "", password: "" });

  const loadDispatchers = () => api.get("/dispatchers").then((r) => setDispatchers(r.data)).catch(() => {});
  useEffect(() => { loadDispatchers(); }, []);

  const addDispatcher = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/dispatchers", { name: dForm.name, email: dForm.email, ...(dForm.password ? { password: dForm.password } : {}) });
      if (!dForm.password && r.data.email_sent === false) toast.error("Dispatcher added, but the invite email could not be sent — check the address or set a password");
      else toast.success(dForm.password ? "Dispatcher added" : "Dispatcher added — set-password email sent");
      setDForm({ name: "", email: "", password: "" });
      loadDispatchers();
    } catch (e2) { toast.error(fmtErr(e2)); }
  };

  const toggleDispatcher = async (d) => {
    try {
      await api.patch(`/dispatchers/${d.id}`, { active: d.active === false });
      toast.success(d.active === false ? "Dispatcher reactivated" : "Dispatcher deactivated — they can no longer sign in");
      loadDispatchers();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!s) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-8">
      <section id="set-markup" className="card-flat space-y-4 p-6" data-testid="markup-settings">
        <p className="label-caps">Subcontractor markup</p>
        <p className="text-sm text-muted-foreground">Applied on top of the sub's cost to set the owner's price.</p>
        <div className="flex gap-2">
          {["percent", "flat"].map((t) => (
            <button key={t} data-testid={`markup-type-${t}`} onClick={() => save({ markup_type: t })}
              className={`h-12 flex-1 rounded-md border text-sm font-bold ${s.markup_type === t ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
              {t === "percent" ? "Percentage" : "Flat amount"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="markup-value" type="number" className="field-input flex-1" value={s.markup_value} onFocus={(e) => e.target.select()}
            onChange={(e) => setS({ ...s, markup_value: Number(e.target.value) })} />
          <button data-testid="markup-save" onClick={() => save({ markup_value: Number(s.markup_value) })} className="h-12 rounded-md bg-primary px-5 text-sm font-bold text-primary-foreground">Save</button>
        </div>
        <label className="flex items-center justify-between rounded-md border border-border p-4">
          <span className="text-sm font-semibold">Show sub's underlying cost to owners</span>
          <button data-testid="toggle-show-cost" onClick={() => save({ show_sub_cost: !s.show_sub_cost })}
            className={`h-8 w-14 rounded-full transition-colors ${s.show_sub_cost ? "bg-primary" : "bg-muted"}`}>
            <span className={`block h-6 w-6 translate-x-1 rounded-full bg-white transition-transform ${s.show_sub_cost ? "translate-x-7" : ""}`} />
          </button>
        </label>
      </section>

      <section id="set-dispatchers" className="card-flat space-y-3 p-6" data-testid="dispatcher-settings">
        <p className="label-caps">Team — dispatchers</p>
        <label className="flex items-center justify-between gap-3 rounded-md border border-border p-4">
          <span className="text-sm font-semibold">Dispatchers can take jobs themselves<span className="block text-xs font-normal text-muted-foreground">They can self-assign a visit and check in/out like crew</span></span>
          <button data-testid="dispatcher-can-work" onClick={() => save({ dispatcher_can_work: !s.dispatcher_can_work })}
            className={`h-8 w-14 shrink-0 rounded-full transition-colors ${s.dispatcher_can_work ? "bg-primary" : "bg-muted"}`}>
            <span className={`block h-6 w-6 translate-x-1 rounded-full bg-white transition-transform ${s.dispatcher_can_work ? "translate-x-7" : ""}`} />
          </button>
        </label>
        <p className="text-sm text-muted-foreground">
          Dispatchers run your day-to-day: they can quote add-ons, assign and dispatch jobs, and manage invoices — but can't touch these settings or billing.
        </p>
        {dispatchers.map((d) => (
          <div key={d.id} className="flex items-center justify-between rounded-md border border-border p-3" data-testid={`dispatcher-row-${d.id}`}>
            <div>
              <p className="text-sm font-semibold">{d.name}</p>
              <p className="text-xs text-muted-foreground">{d.email}</p>
            </div>
            <button data-testid={`dispatcher-toggle-${d.id}`} onClick={() => toggleDispatcher(d)}
              className={`h-10 rounded-md border px-3 text-xs font-bold ${d.active === false ? "border-primary text-primary" : "border-destructive text-destructive"}`}>
              {d.active === false ? "Reactivate" : "Deactivate"}
            </button>
          </div>
        ))}
        <form onSubmit={addDispatcher} className="space-y-2" data-testid="add-dispatcher-form">
          <input data-testid="dispatcher-name" className="field-input w-full" placeholder="Dispatcher name" value={dForm.name}
            onChange={(e) => setDForm({ ...dForm, name: e.target.value })} required />
          <input data-testid="dispatcher-email" type="email" className="field-input w-full" placeholder="Dispatcher email" value={dForm.email}
            onChange={(e) => setDForm({ ...dForm, email: e.target.value })} required />
          <input data-testid="dispatcher-password" type="text" className="field-input w-full" placeholder="Password (optional — blank emails them a set-password link)" value={dForm.password}
            onChange={(e) => setDForm({ ...dForm, password: e.target.value })} />
          <button data-testid="dispatcher-add" className="btn-primary-big !h-14 !text-base">Add dispatcher</button>
        </form>
      </section>

      <section id="set-commissions" className="card-flat space-y-3 p-6" data-testid="lead-commission-settings">
        <p className="label-caps">Lead generation commissions</p>
        <p className="text-sm text-muted-foreground">Crew and dispatchers earn a flat bonus for each lead you mark warm, plus a percent of revenue when a lead becomes a customer at or above your threshold.</p>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span>$</span>
          <input data-testid="lead-warm-bonus" type="number" min="0" className="field-input w-20" value={s.lead_warm_bonus ?? 10} onFocus={(e) => e.target.select()}
            onChange={(e) => setS({ ...s, lead_warm_bonus: Number(e.target.value) })} />
          <span>per warm lead</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <input data-testid="lead-convert-pct" type="number" min="0" max="100" className="field-input w-20" value={s.lead_convert_pct ?? 5} onFocus={(e) => e.target.select()}
            onChange={(e) => setS({ ...s, lead_convert_pct: Number(e.target.value) })} />
          <span>% of revenue when converted at $</span>
          <input data-testid="lead-convert-threshold" type="number" min="0" className="field-input w-24" value={s.lead_convert_threshold ?? 500} onFocus={(e) => e.target.select()}
            onChange={(e) => setS({ ...s, lead_convert_threshold: Number(e.target.value) })} />
          <span>or more</span>
        </div>
        <button data-testid="lead-commission-save" onClick={() => save({ lead_warm_bonus: Number(s.lead_warm_bonus ?? 10), lead_convert_pct: Number(s.lead_convert_pct ?? 5), lead_convert_threshold: Number(s.lead_convert_threshold ?? 500) })}
          className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">Save commission settings</button>
      </section>

      <section id="set-invite" className="card-flat space-y-3 p-6" data-testid="team-settings">
        <p className="label-caps">Your invite code</p>
        <p className="text-sm text-muted-foreground">Owners and subcontractors enter this when they sign up so their accounts link to your business.</p>
        <p className="rounded-md bg-muted p-4 text-center font-mono text-2xl font-bold tracking-widest" data-testid="invite-code">{user?.invite_code || "—"}</p>
      </section>
    </div>
  );
}
